import { Spinner } from "components";
import { useState } from "react";
import { toast } from "sonner";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import { getForexCancelContext } from "service/moneeflo/legacy-forex-order-api";
import { refundForexOrder } from "../apis";
import { formatInrMinor, inputToMinor, minorToInput } from "pages/GenericOrderDetailsPage/utils/money";
import { useForexAccess } from "pages/GenericOrderDetailsPage/hooks/useForexAccess";

const refundReasons = [
  "Incomplete / incorrect documentation",
  "Mismatch in traveler details",
  "Customer not willing to pay TCS",
  "Wrong product selected",
  "Change of travel plan / trip cancelled",
  "Did not like the quality of currency notes",
];

const field = "w-full rounded-lg border border-gray-300 bg-white px-3 py-3 text-sm text-gray-900 focus:border-[#1B4ED7] focus:outline-none focus:ring-1 focus:ring-[#1B4ED7]";

/**
 * Giving money back on a forex order. The amount starts at what core says can go back (after any
 * cancellation charge, when the person comes from the cancel page) and cannot go above what core
 * allows in one refund. Every figure is paise: nothing is added up here.
 */
export default function ForexRefund() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canRefund } = useForexAccess();
  const location = useLocation();
  const carried = (location.state ?? {}) as { cancelReason?: string; refundMinor?: string };

  const { data: ctx, isLoading, error: loadError } = useQuery({
    queryKey: ["refundContext", id],
    queryFn: getForexCancelContext,
  });

  const [reason, setReason] = useState(carried.cancelReason ?? "");
  const [amount, setAmount] = useState<string | null>(null);
  const [reference, setReference] = useState("");
  const [error, setError] = useState<string | null>(null);

  const startMinor = carried.refundMinor && /^\d+$/.test(carried.refundMinor) ? carried.refundMinor : ctx?.refundableMinor ?? "0";
  const shown = amount ?? minorToInput(startMinor);
  const minor = inputToMinor(shown);
  const largest = ctx ? BigInt(ctx.largestSingleRefundMinor) : 0n;
  const tooMuch = minor !== null && ctx !== undefined && minor > largest;
  const ready = !!ctx && reason !== "" && reference.trim().length > 0 && minor !== null && minor > 0n && !tooMuch;

  const { mutate, isLoading: saving } = useMutation(refundForexOrder, {
    onSuccess: () => {
      toast.success("Refund recorded");
      navigate(`/forex/orderdetails/${id}`);
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "The refund could not be recorded."),
  });

  if (!canRefund) {
    return (
      <div role="alert" className="m-3 rounded-lg bg-white-A700 p-6 text-sm text-[#333333]">
        <h1 className="mb-2 text-lg font-semibold">You cannot refund this order</h1>
        <p>Your role does not include giving money back. Ask a manager if you need this.</p>
      </div>
    );
  }

  return (
    <>
      {(isLoading || saving) && <Spinner />}
      <div className="sm:p-1 flex md:flex-col font-inter items-start justify-between gap-5 mx-auto w-full h-full md:h-auto">
        <section className="flex w-full flex-col gap-5 rounded-lg bg-white-A700 p-3">
          <form
            className="flex max-w-md flex-col gap-5 px-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (!ready || minor === null) return;
              setError(null);
              mutate({ amountMinor: minor.toString(), reason, reference });
            }}
          >
            <h1 className="text-lg font-semibold text-[#333333]">{ctx ? `Refund order OD-${ctx.reference}` : "Refund order"}</h1>
            {loadError ? (
              <p role="alert" className="text-sm text-[#7A1F1F]">
                {loadError instanceof Error ? loadError.message : "The order could not be loaded."}
              </p>
            ) : null}
            {error ? (
              <p role="alert" className="rounded-lg border border-[#E8B4B4] bg-[#FFF3F3] px-3 py-2 text-sm text-[#7A1F1F]">
                {error}
              </p>
            ) : null}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="refund-reason" className="text-sm font-medium text-gray-700">Reason for the refund</label>
              <select id="refund-reason" className={field} value={reason} onChange={(e) => setReason(e.target.value)}>
                <option value="">Select a reason</option>
                {refundReasons.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="refund-amount" className="text-sm font-medium text-gray-700">Amount (₹)</label>
              <input
                id="refund-amount"
                inputMode="decimal"
                className={field}
                value={shown}
                aria-invalid={tooMuch}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) setAmount(v);
                }}
              />
              {ctx ? (
                <p className="text-xs text-[#555555]">One refund can be up to {formatInrMinor(ctx.largestSingleRefundMinor)}.</p>
              ) : null}
              {tooMuch ? (
                <p role="alert" className="text-sm text-[#B00020]">That is more than can be refunded in one go.</p>
              ) : null}
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="refund-reference" className="text-sm font-medium text-gray-700">Bank reference / UTR</label>
              <input id="refund-reference" className={field} value={reference} onChange={(e) => setReference(e.target.value)} autoComplete="off" />
            </div>
            <button
              type="submit"
              disabled={!ready}
              className="w-fit cursor-pointer rounded-lg border border-blue-700 bg-blue-700 px-4 py-[11px] text-sm font-semibold text-white-A700 hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Record refund
            </button>
          </form>
        </section>

        <section className="w-1/3 md:w-full rounded-lg bg-white-A700 p-3">
          <div className="w-full rounded-xl bg-white p-6 shadow-[0px_4px_4px_2px_rgba(239,241,251,1)]">
            <h2 className="mb-6 text-2xl font-bold">Calculation</h2>
            <div className="flex flex-col gap-5">
              <div className="flex items-center justify-between px-[15px] py-[17px] bg-[#f2f6ff]">
                <span className="text-base font-semibold">Order total</span>
                <span className="text-base font-medium text-[#333333]">{formatInrMinor(ctx?.totalPayableMinor)}</span>
              </div>
              <div className="flex items-center justify-between px-[15px] text-sm text-[#333333]">
                <span>Received so far</span>
                <span className="font-medium">{formatInrMinor(ctx?.paidMinor)}</span>
              </div>
              <div className="flex items-center justify-between rounded-b-xl border-t-2 border-[#176bef] bg-[#f2f6ff] px-[15px] py-[17px]">
                <span className="text-base font-semibold text-[#1456c4]">Can be refunded</span>
                <span className="text-base font-medium text-[#1456c4]">{formatInrMinor(ctx?.refundableMinor)}</span>
              </div>
              <p className="text-xs text-[#555555]">The amount that can go back is worked out by the system from the payments received.</p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
